import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Modal from '../UI/Modal';
import gameHistoryService from '../../api/services/gameHistory.service';
import {
  TrophyIcon,
  UsersIcon,
  ClockIcon,
  CurrencyDollarIcon,
  TicketIcon,
  ChartBarIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';

interface GameHistoryDetailModalProps {
  isOpen: boolean;
  gameId: string | null;
  onClose: () => void;
}

const StatBox: React.FC<{
  label: string;
  value: string | number;
  icon: React.ReactNode;
  color?: string;
}> = ({ label, value, icon, color = 'text-primary-500' }) => (
  <div className="border border-gray-100 rounded-lg p-3">
    <div className="flex items-center gap-2 mb-1">
      <span className={color}>{icon}</span>
      <p className="text-[10px] text-gray-500 uppercase tracking-wide">{label}</p>
    </div>
    <p className="text-base font-bold text-gray-900">{value}</p>
  </div>
);

const formatDuration = (ms: number): string => {
  if (!ms || ms <= 0) return '0s';
  const totalSec = Math.floor(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  if (min > 0) return `${min}m ${sec}s`;
  return `${sec}s`;
};

const fmtAmount = (n: number | undefined | null): string => {
  if (n == null || !Number.isFinite(n)) return '0';
  const rounded = Math.round((n + Number.EPSILON) * 1000) / 1000;
  return rounded.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  });
};

/* =========================================================
   CLIENT-SIDE WINNING CELL RESOLVER
   Mirrors the server-side pattern enumeration so we can
   always highlight the winning pattern even when the
   server-stored `winningCells` array is empty or stale.
========================================================= */

type GridShape = {
  B: (number | null)[];
  I: (number | null)[];
  N: (number | null)[];
  G: (number | null)[];
  O: (number | null)[];
};

const LETTERS = ['B', 'I', 'N', 'G', 'O'] as const;

interface Cell {
  letter: (typeof LETTERS)[number];
  row: number;
}

function rowCells(row: number): Cell[] {
  return LETTERS.map((letter) => ({ letter, row }));
}

function colCells(col: number): Cell[] {
  return [0, 1, 2, 3, 4].map((row) => ({
    letter: LETTERS[col],
    row,
  }));
}

const DIAG_MAIN: Cell[] = [
  { letter: 'B', row: 0 },
  { letter: 'I', row: 1 },
  { letter: 'N', row: 2 },
  { letter: 'G', row: 3 },
  { letter: 'O', row: 4 },
];

const DIAG_ANTI: Cell[] = [
  { letter: 'O', row: 0 },
  { letter: 'G', row: 1 },
  { letter: 'N', row: 2 },
  { letter: 'I', row: 3 },
  { letter: 'B', row: 4 },
];

const CORNERS: Cell[] = [
  { letter: 'B', row: 0 },
  { letter: 'O', row: 0 },
  { letter: 'B', row: 4 },
  { letter: 'O', row: 4 },
];

const SINGLE_MIDDLE: Cell[] = [
  { letter: 'I', row: 1 },
  { letter: 'G', row: 1 },
  { letter: 'I', row: 3 },
  { letter: 'G', row: 3 },
];

const MIDDLE_CROSS: Cell[] = [
  { letter: 'I', row: 2 },
  { letter: 'N', row: 1 },
  { letter: 'N', row: 3 },
  { letter: 'G', row: 2 },
];

function fullCells(): Cell[] {
  const out: Cell[] = [];
  for (const letter of LETTERS) {
    for (let row = 0; row < 5; row++) out.push({ letter, row });
  }
  return out;
}

/**
 * Given the stored `pattern` / `patternName` string and the
 * winner's grid, return the set of cells that belong to the
 * winning pattern.
 *
 * The pattern strings match what history.service.ts writes:
 *   "HORIZONTAL ROW 3"
 *   "VERTICAL COLUMN N"
 *   "DIAGONAL ↘"  /  "DIAGONAL ↙"
 *   "FOUR CORNERS"
 *   "SINGLE MIDDLE"
 *   "MIDDLE CROSS"
 *   "FULL CLOSE"  /  "FULL HOUSE"
 *
 * Because a winner can have multiple winning patterns joined
 * with " + ", we parse each fragment and union the cells.
 */
function deriveWinningCells(
  patternRaw: string | undefined | null,
  patternNameRaw: string | undefined | null,
  grid: GridShape
): Cell[] {
  const text = `${patternRaw ?? ''} ${patternNameRaw ?? ''}`.trim();
  if (!text) return [];

  const parts = text
    .split('+')
    .map((p) => p.trim())
    .filter(Boolean);

  const out: Cell[] = [];
  const seen = new Set<string>();
  const push = (c: Cell) => {
    const k = `${c.letter}-${c.row}`;
    if (seen.has(k)) return;
    seen.add(k);
    out.push(c);
  };

  for (const raw of parts) {
    const p = raw.toUpperCase();

    // FULL
    if (p.includes('FULL')) {
      for (const c of fullCells()) push(c);
      continue;
    }

    // FOUR CORNERS
    if (p.includes('CORNER')) {
      for (const c of CORNERS) push(c);
      continue;
    }

    // SINGLE MIDDLE
    if (p.includes('SINGLE MIDDLE')) {
      for (const c of SINGLE_MIDDLE) push(c);
      continue;
    }

    // MIDDLE CROSS
    if (p.includes('MIDDLE CROSS')) {
      for (const c of MIDDLE_CROSS) push(c);
      continue;
    }

    // HORIZONTAL ROW n
    const rowMatch = p.match(/ROW\s+(\d+)/);
    if (rowMatch) {
      const r = Number(rowMatch[1]) - 1;
      if (r >= 0 && r < 5) for (const c of rowCells(r)) push(c);
      continue;
    }

    // VERTICAL COLUMN X
    const colMatch = p.match(/COLUMN\s+([BINGO])/);
    if (colMatch) {
      const col = LETTERS.indexOf(colMatch[1] as any);
      if (col >= 0) for (const c of colCells(col)) push(c);
      continue;
    }

    // DIAGONAL ↘ / ↙
    if (p.includes('DIAGONAL')) {
      // Prefer the arrow when present; otherwise try both and
      // keep the one whose cells are actually called.
      if (p.includes('↘')) {
        for (const c of DIAG_MAIN) push(c);
      } else if (p.includes('↙')) {
        for (const c of DIAG_ANTI) push(c);
      } else {
        for (const c of DIAG_MAIN) push(c);
        for (const c of DIAG_ANTI) push(c);
      }
      continue;
    }

    // Generic fallback: if we can't identify the pattern, leave
    // it to the server-provided winningCells array.
  }

  // Safety net: if parsing produced nothing usable, fall back
  // to the server's own winningCells (if any).
  return out;
}

const GameHistoryDetailModal: React.FC<GameHistoryDetailModalProps> = ({
  isOpen,
  gameId,
  onClose,
}) => {
  const [expandedWinner, setExpandedWinner] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'winners' | 'players' | 'numbers'>(
    'winners'
  );

  const { data, isLoading, error } = useQuery({
    queryKey: ['game-history-detail', gameId],
    queryFn: () => gameHistoryService.getOne(gameId as string),
    enabled: isOpen && !!gameId,
    staleTime: 60 * 1000,
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Game History Details" size="lg">
      {isLoading && (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-solid border-primary-500 border-t-transparent" />
        </div>
      )}

      {error && (
        <p className="text-sm text-red-500 py-4">
          {error instanceof Error ? error.message : 'Failed to load game history'}
        </p>
      )}

      {data && (
        <div className="space-y-5">
          {/* Header */}
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {data.historyId}
              </h3>
              <p className="text-xs text-gray-500">
                Room: <span className="font-medium">{data.roomId}</span> · Bet: ETB{' '}
                {fmtAmount(data.betAmount)}
              </p>
              <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                <span className="inline-flex items-center gap-1">
                  <ClockIcon className="w-3.5 h-3.5" />
                  {formatDuration(data.durationMs)}
                </span>
                <span>·</span>
                <span>
                  {new Date(data.startedAt).toLocaleString()} →{' '}
                  {new Date(data.finishedAt).toLocaleString()}
                </span>
              </div>
            </div>
            <div className="text-right">
              <p className="text-[10px] text-gray-400">Platform Revenue</p>
              <p className="text-lg font-bold text-green-600">
                ETB {fmtAmount(data.platformRevenue)}
              </p>
            </div>
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            <StatBox
              label="Players"
              value={data.playersCount.toLocaleString()}
              icon={<UsersIcon className="w-4 h-4" />}
            />
            <StatBox
              label="Cartelas Sold"
              value={data.totalCartelasSold.toLocaleString()}
              icon={<TicketIcon className="w-4 h-4" />}
              color="text-secondary-500"
            />
            <StatBox
              label="Prize Pool"
              value={`ETB ${fmtAmount(data.prizePool)}`}
              icon={<CurrencyDollarIcon className="w-4 h-4" />}
              color="text-green-500"
            />
            <StatBox
              label="Net Prize"
              value={`ETB ${fmtAmount(data.netPrizePool)}`}
              icon={<CurrencyDollarIcon className="w-4 h-4" />}
              color="text-green-500"
            />
            <StatBox
              label="Numbers Called"
              value={data.callIndex.toLocaleString()}
              icon={<ChartBarIcon className="w-4 h-4" />}
              color="text-primary-500"
            />
            <StatBox
              label="Winners"
              value={data.winners.length.toLocaleString()}
              icon={<TrophyIcon className="w-4 h-4" />}
              color="text-yellow-500"
            />
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-100">
            <div className="flex gap-1">
              {[
                { key: 'winners', label: `Winners (${data.winners.length})` },
                { key: 'players', label: `Players (${data.players.length})` },
                { key: 'numbers', label: `Numbers (${data.calledNumbers.length})` },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key as any)}
                  className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
                    activeTab === tab.key
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Winners tab */}
          {activeTab === 'winners' && (
            <div className="space-y-2">
              {data.winners.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  No winners in this game
                </p>
              ) : (
                data.winners.map((w, idx) => {
                  const calledSet = new Set(data.calledNumbers);

                  // ── Winning cells derivation ──
                  // Prefer client-side derivation from the pattern
                  // label so green ALWAYS shows. Fall back to the
                  // server's `winningCells` if parsing found nothing.
                  let winningCells: Cell[] = [];
                  if (w.grid) {
                    winningCells = deriveWinningCells(
                      w.pattern,
                      w.patternName,
                      w.grid as GridShape
                    );
                  }

                  if (winningCells.length === 0 && w.winningCells?.length) {
                    winningCells = w.winningCells.map((c) => ({
                      letter: c.letter as any,
                      row: c.row,
                    }));
                  }

                  const winSet = new Set(
                    winningCells.map((c) => `${c.letter}-${c.row}`)
                  );

                  return (
                    <div
                      key={idx}
                      className="border border-gray-100 rounded-lg overflow-hidden"
                    >
                      <button
                        onClick={() =>
                          setExpandedWinner(
                            expandedWinner === idx ? null : idx
                          )
                        }
                        className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-yellow-50 flex items-center justify-center">
                            <TrophyIcon className="w-4 h-4 text-yellow-600" />
                          </div>
                          <div className="text-left">
                            <p className="text-sm font-medium text-gray-900">
                              {w.displayName}
                            </p>
                            <p className="text-[10px] text-gray-500">
                              @{w.telegramId} · Cartela #{w.cartelaNumber} ·{' '}
                              {w.patternName || w.pattern}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className="text-sm font-bold text-green-600">
                              ETB {fmtAmount(w.prizeAmount)}
                            </p>
                            <p className="text-[10px] text-gray-500">
                              Call #{w.callIndex}
                            </p>
                          </div>
                          {expandedWinner === idx ? (
                            <ChevronUpIcon className="w-4 h-4 text-gray-400" />
                          ) : (
                            <ChevronDownIcon className="w-4 h-4 text-gray-400" />
                          )}
                        </div>
                      </button>

                      {expandedWinner === idx && w.grid && (
                        <div className="p-3 bg-gray-50 border-t border-gray-100">
                          <div className="flex items-center gap-2 mb-3 text-[11px]">
                            <span className="text-gray-500">Balance:</span>
                            <span className="font-medium text-gray-700">
                              ETB {fmtAmount(w.balanceBefore ?? 0)}
                            </span>
                            <ArrowRightIcon className="w-3 h-3 text-gray-400" />
                            <span className="font-semibold text-green-600">
                              ETB {fmtAmount(w.balanceAfter ?? 0)}
                            </span>
                          </div>

                          <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-2">
                            Winning Grid
                          </p>
                          <div className="inline-block">
                            <div className="grid grid-cols-5 gap-1">
                              {(['B', 'I', 'N', 'G', 'O'] as const).map(
                                (letter) => (
                                  <div
                                    key={letter}
                                    className="w-8 h-8 flex items-center justify-center text-xs font-bold text-primary-600 bg-primary-50 rounded"
                                  >
                                    {letter}
                                  </div>
                                )
                              )}
                              {[0, 1, 2, 3, 4].map((row) =>
                                (['B', 'I', 'N', 'G', 'O'] as const).map(
                                  (letter) => {
                                    const val =
                                      w.grid?.[letter]?.[row] ?? null;
                                    const isWinning = winSet.has(
                                      `${letter}-${row}`
                                    );
                                    const isCalled =
                                      val !== null && calledSet.has(val);

                                    // 🟩 winning pattern
                                    // 🟨 called, not winning
                                    // ⬜ not called
                                    // ⬛ free space
                                    let cls =
                                      'bg-white border border-gray-200 text-gray-700';
                                    if (isWinning) {
                                      cls =
                                        'bg-green-500 text-white font-bold shadow-sm';
                                    } else if (isCalled) {
                                      cls =
                                        'bg-yellow-400 text-yellow-900 font-semibold';
                                    } else if (val === null) {
                                      cls =
                                        'bg-gray-200 text-gray-400';
                                    }

                                    return (
                                      <div
                                        key={`${letter}-${row}`}
                                        className={`w-8 h-8 flex items-center justify-center text-xs font-medium rounded ${cls}`}
                                      >
                                        {val === null ? '★' : val}
                                      </div>
                                    );
                                  }
                                )
                              )}
                            </div>
                          </div>

                          <div className="flex flex-wrap items-center gap-3 mt-3 text-[10px] text-gray-500">
                            <span className="inline-flex items-center gap-1">
                              <span className="w-3 h-3 rounded bg-green-500" />
                              Winning pattern
                            </span>
                            <span className="inline-flex items-center gap-1">
                              <span className="w-3 h-3 rounded bg-yellow-400" />
                              Called
                            </span>
                            <span className="inline-flex items-center gap-1">
                              <span className="w-3 h-3 rounded bg-white border border-gray-200" />
                              Not called
                            </span>
                            <span className="inline-flex items-center gap-1">
                              <span className="w-3 h-3 rounded bg-gray-200" />
                              Free
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* Players tab */}
          {activeTab === 'players' && (
            <div className="space-y-1.5 max-h-80 overflow-y-auto">
              {data.players.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  No players in this game
                </p>
              ) : (
                data.players.map((p, idx) => {
                  const net = (p.totalWon ?? 0) - (p.totalBet ?? 0);
                  return (
                    <div
                      key={idx}
                      className="flex items-center gap-3 px-3 py-2 border border-gray-100 rounded-lg"
                    >
                      <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 text-[10px] font-semibold shrink-0">
                        {p.displayName
                          .split(' ')
                          .map((n) => n[0])
                          .join('')
                          .slice(0, 2)
                          .toUpperCase() || '?'}
                      </div>

                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-gray-900 truncate">
                          {p.displayName}
                          {p.wonCartelas.length > 0 && (
                            <span className="ml-1.5 text-[10px] text-green-600 font-medium">
                              🏆 {p.wonCartelas.join(',')}
                            </span>
                          )}
                        </p>
                        <p className="text-[10px] text-gray-500 truncate">
                          @{p.telegramId} · #{p.cartelaNumbers.join(',') || '—'}
                        </p>
                      </div>

                      <div className="flex items-center gap-2 text-[10px] whitespace-nowrap">
                        <span className="text-gray-500">
                          {fmtAmount(p.balanceBefore ?? 0)}
                        </span>
                        <ArrowRightIcon className="w-2.5 h-2.5 text-gray-400" />
                        <span className="text-gray-700 font-medium">
                          {fmtAmount(p.balanceAfter ?? 0)}
                        </span>
                        <span className="text-red-500">
                          −{fmtAmount(p.totalBet ?? 0)}
                        </span>
                        <span className="text-green-600 font-medium">
                          +{fmtAmount(p.totalWon ?? 0)}
                        </span>
                        <span
                          className={`font-semibold ${
                            net >= 0 ? 'text-green-600' : 'text-red-500'
                          }`}
                        >
                          {net >= 0 ? '+' : ''}
                          {fmtAmount(net)}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* Numbers tab */}
          {activeTab === 'numbers' && (
            <div>
              <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-2">
                Called Numbers Sequence
              </p>
              <div className="flex flex-wrap gap-1.5 max-h-80 overflow-y-auto p-1">
                {data.calledNumbers.length === 0 ? (
                  <p className="text-sm text-gray-500 py-4">
                    No numbers called
                  </p>
                ) : (
                  data.calledNumbers.map((num, idx) => (
                    <div
                      key={idx}
                      className="w-9 h-9 flex items-center justify-center text-xs font-semibold rounded-full bg-primary-50 text-primary-700 border border-primary-100"
                      title={`Call #${idx + 1}`}
                    >
                      {num}
                    </div>
                  ))
                )}
              </div>
              {data.remainingNumbers.length > 0 && (
                <>
                  <p className="text-[10px] text-gray-500 uppercase tracking-wide mt-4 mb-2">
                    Remaining ({data.remainingNumbers.length})
                  </p>
                  <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto p-1">
                    {data.remainingNumbers.map((num, idx) => (
                      <div
                        key={idx}
                        className="w-9 h-9 flex items-center justify-center text-xs font-medium rounded-full bg-gray-100 text-gray-500"
                      >
                        {num}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default GameHistoryDetailModal;